import { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import {
  ButtonIcon,
  FlexContainer,
  LabelBase,
  TableLegacy,
  useTimezone,
} from '@facephi/ui-react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import { useEmptyState } from '../../hooks/useEmptyState';
import { usePagination } from '../../hooks/usePagination';
import {
  MAX_ITEMS_PAGE,
  OPERATIONS_COLORS,
  RoutesName,
} from '../../state/constants';
import { CiamUserPage } from '../../state/model/user';
import { getCiamUsers } from '../../state/queries/users';
import { ImageViewer } from '../imageViewer';
import { StyledAavatar } from './Styles';

export const UserList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { formatTimezone } = useTimezone();

  const {
    data: { searchCiamUsers } = {},
    loading,
    error,
    fetchMore,
  } = useQuery<{
    searchCiamUsers: CiamUserPage;
  }>(getCiamUsers, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      parameters: {
        limit: MAX_ITEMS_PAGE,
      },
    },
  });

  const usersData = useMemo(() => {
    return (
      searchCiamUsers?.edges?.map((item) => ({
        user: (
          <StyledAavatar columnGap="1.8" alignItems="center">
            <ImageViewer url={item?.node?.selfiePath} isSmall />
            {t(item.node.name)}
          </StyledAavatar>
        ),
        date: formatTimezone(item?.node?.creationTime, 'DD/MM/YYYY HH:mm:ss'),
        userId: t(item.node.userId),
        operations:
          item?.node?.operationFlags?.isAuthentication ||
          item?.node?.operationFlags?.isOnboarding ||
          item?.node?.operationFlags?.isTeseo ? (
            <FlexContainer columnGap="0.8">
              {item.node?.operationFlags?.isAuthentication && (
                <LabelBase
                  key={`authentication-${item.node.userId}`}
                  color={OPERATIONS_COLORS.authentication}
                  label={t('authentication')}
                />
              )}
              {item.node?.operationFlags?.isOnboarding && (
                <LabelBase
                  key={`onboarding-${item.node.userId}`}
                  color={OPERATIONS_COLORS.onboarding}
                  label={t('onboarding')}
                />
              )}
              {item.node?.operationFlags?.isTeseo && (
                <LabelBase
                  key={`teseo-${item.node.userId}`}
                  color={OPERATIONS_COLORS.teseo}
                  label={t('teseo')}
                />
              )}
            </FlexContainer>
          ) : (
            <></>
          ),
        action: (
          <ButtonIcon
            iconName="Eye"
            variant="text"
            onClick={() =>
              navigate(
                pathname.replace(
                  RoutesName.users,
                  RoutesName.userDetail.replace(':id', item.node.userId)
                )
              )
            }
          />
        ),
      })) || []
    );
  }, [searchCiamUsers]);

  const { handlePagination } = usePagination(
    loading,
    fetchMore,
    searchCiamUsers?.pageInfo.hasNextPage,
    searchCiamUsers?.pageInfo.endCursor
  );

  const { emptyState } = useEmptyState({
    data: usersData,
    hasError: Boolean(error),
    isLoading: loading,
  });

  const columns = useMemo(
    () => [
      {
        Header: t('User'),
        accessor: 'user',
        maxWidth: 300,
        style: { paddingLeft: '1rem' },
      },
      {
        Header: t('Date'),
        accessor: 'date',
        maxWidth: 200,
      },
      {
        Header: t('User Id'),
        accessor: 'userId',
        maxWidth: 300,
      },
      {
        Header: t('Operations'),
        accessor: 'operations',
      },
      {
        Header: t('Action'),
        accessor: 'action',
        maxWidth: 100,
      },
    ],
    []
  );

  return (
    <>
      <TableLegacy
        columns={columns}
        data={usersData}
        emptyStateData={emptyState}
        loading={loading}
        hasMore={searchCiamUsers?.pageInfo.hasNextPage}
        fetchMore={handlePagination}
      />
    </>
  );
};
